import {announce} from '@github-ui/aria-live'
import {ListView} from '@github-ui/list-view'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {Spinner} from '@primer/react'
import {type ReactNode, useCallback, useEffect, useMemo, useRef} from 'react'

import {LABELS} from '../../constants/labels'
import {useDeleteSavedViewDialog} from '../../hooks/use-delete-saved-view-dialog'
import {pickPostDeleteFocusTargetId} from './pick-post-delete-focus-target'
import styles from './RepositoryViews.module.css'
import {resolvePostDeleteFocusTarget} from './resolve-post-delete-focus-target'
import type {SavedView} from './types'
import {ViewRow} from './ViewRow'
import type {ViewsSortValue} from './views-sort-options'
import {ViewsSortMenu} from './ViewsSortMenu'

type ViewsListProps = {
  views: SavedView[]
  owner: string
  repo: string
  sort: ViewsSortValue
  onSortChange: (value: ViewsSortValue) => void
  isLoading?: boolean
  emptyState?: ReactNode
  viewerCanManageViews?: boolean
}

export function ViewsList({
  views,
  owner,
  repo,
  sort,
  onSortChange,
  isLoading = false,
  emptyState,
  viewerCanManageViews = false,
}: ViewsListProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const deleteReturnFocusRef = useRef<HTMLElement | null>(null)
  const shouldFocusHeadingAfterDeleteRef = useRef(false)

  const handleDeleted = useCallback(
    (deletedView: SavedView) => {
      const target = pickPostDeleteFocusTargetId(views, deletedView.id) ?? 'heading'
      if (target === 'heading') {
        deleteReturnFocusRef.current = null
        shouldFocusHeadingAfterDeleteRef.current = true
      } else {
        deleteReturnFocusRef.current = resolvePostDeleteFocusTarget(target, containerRef.current)
        shouldFocusHeadingAfterDeleteRef.current = false
      }

      announce(
        target === 'heading'
          ? LABELS.views.deleteSuccessLastView(deletedView.name)
          : LABELS.views.deleteSuccess(deletedView.name),
        {assertive: true},
      )
    },
    [views],
  )

  const {openDeleteDialog, deleteDialog, isDeleteDialogOpen} = useDeleteSavedViewDialog({
    owner,
    repo,
    onDeleted: handleDeleted,
    returnFocusRef: deleteReturnFocusRef,
  })

  // Deleting the last view leaves no row to focus, so focus returns to the page heading. React runs
  // the unmounting dialog's focus-trap cleanup before this effect, so the heading focus lands last
  // and isn't overwritten with `document.body`.
  useEffect(() => {
    if (isDeleteDialogOpen || !shouldFocusHeadingAfterDeleteRef.current) return

    shouldFocusHeadingAfterDeleteRef.current = false
    resolvePostDeleteFocusTarget('heading', null)?.focus()
  }, [isDeleteDialogOpen])

  const actions = useMemo(
    () => [
      {
        key: 'views-sort-menu',
        render: () => <ViewsSortMenu sort={sort} onSortChange={onSortChange} />,
      },
    ],
    [sort, onSortChange],
  )

  const itemCount = isLoading ? undefined : LABELS.views.itemCount(views.length)
  const metadata = (
    <ListViewMetadata
      title={itemCount}
      actions={actions}
      actionsLabel={LABELS.views.actionsLabel}
      assistiveAnnouncement={isLoading ? LABELS.views.loadingSavedViews : undefined}
    />
  )

  return (
    <div ref={containerRef}>
      <ListView title={LABELS.views.viewsListTitle} metadata={metadata} titleHeaderTag="h2" emptyState={emptyState}>
        {isLoading ? (
          <li className={styles.loadingContainer}>
            <Spinner size="large" srText={null} />
          </li>
        ) : (
          views.map(view => (
            <ViewRow
              key={view.id}
              view={view}
              owner={owner}
              repo={repo}
              canManage={viewerCanManageViews}
              onRequestDelete={openDeleteDialog}
            />
          ))
        )}
      </ListView>
      {deleteDialog}
    </div>
  )
}
